/* eslint-disable */
/**
 * SammenklappeligSektion — fold-ud/fold-ind wrapper med Framer Motion.
 *
 * Props:
 *   titel        string   — sektions-overskrift
 *   defaultÅben  bool     — start åben? (default: true)
 *   children     ReactNode
 *   isMobil      bool     — på desktop (false) renders children altid uden animation/wrapper
 *   style        object   — ekstra styles på container-div
 *
 * Designregel:
 *   - Desktop (isMobil=false): render children direkte, INGEN klik-interaktion.
 *   - Mobil (isMobil=true): vis header med chevron, animer height via Framer Motion.
 *   - Unmount ALDRIG children ved lukning — brug overflow:hidden + height=0.
 *     Dette sikrer at data-noder (polling-hooks) forbliver monteret og ikke
 *     bryder in-place-opdatering (se Trin-3-krav om scroll-fix-mekanikken).
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { TEKST, KANT, RADIUS, FONT } from './tokens';

export default function SammenklappeligSektion({
  titel,
  defaultÅben = true,
  children,
  isMobil = false,
  style = {},
}) {
  const [åben, setÅben] = useState(defaultÅben);

  // Stabilt ID til aria-controls (slugified titel)
  const sektionId = `sammenklap-${(titel ?? 'sektion').replace(/\s+/g, '-').toLowerCase()}`;

  // Desktop: render direkte — ingen wrapper, ingen klik, ingen animation
  if (!isMobil) {
    return <div style={style}>{children}</div>;
  }

  return (
    <div style={{ marginBottom: 4, ...style }}>
      {/* Klik-header — kun på mobil */}
      <button
        onClick={() => setÅben(v => !v)}
        aria-expanded={åben}
        aria-controls={sektionId}
        style={{
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'space-between',
          width:          '100%',
          background:     'none',
          border:         'none',
          borderBottom:   åben ? `1px solid ${KANT.fin}` : 'none',
          padding:        '10px 0',
          cursor:         'pointer',
          fontFamily:     FONT.family,
          // Minimum tap-target 44px
          minHeight:      44,
        }}
      >
        <span style={{
          fontSize:    13,
          fontWeight:  700,
          color:       TEKST.sekundær,
          letterSpacing: 0.3,
          textTransform: 'uppercase',
        }}>
          {titel}
        </span>
        <motion.span
          animate={{ rotate: åben ? 180 : 0 }}
          transition={{ duration: 0.22, ease: 'easeInOut' }}
          style={{ display: 'flex', alignItems: 'center', color: TEKST.sekundær }}
        >
          <ChevronDown size={16} />
        </motion.span>
      </button>

      {/* Fold-animeret indhold — children forbliver monteret (overflow:hidden) */}
      <motion.div
        id={sektionId}
        initial={false}
        animate={{
          height: åben ? 'auto' : 0,
          opacity: åben ? 1 : 0,
        }}
        transition={{ duration: 0.26, ease: [0.4, 0, 0.2, 1] }}
        style={{
          overflow: 'hidden',
          // Ingen display:none — children unmountes ALDRIG
        }}
      >
        <div style={{ paddingTop: 12 }}>
          {children}
        </div>
      </motion.div>
    </div>
  );
}
